import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import Swal from "sweetalert2";

const SettingsPage = () => {
  const { api, showToast, settings: globalSettings } = useAuth();
  const [settings, setSettings] = useState({
    system_name: "",
    organization: "",
    logo_url: "",
    event_date: "",
    allow_signature: "true",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.settings.getAll();
      if (response.success) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      showToast("เกิดข้อผิดพลาดในการโหลดการตั้งค่า", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === "checkbox" ? (checked ? "true" : "false") : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = await Swal.fire({
      icon: "question",
      title: "ยืนยันการบันทึก",
      text: "คุณต้องการบันทึกการตั้งค่าใช่หรือไม่?",
      showCancelButton: true,
      confirmButtonText: "ใช่, บันทึก",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#007CC7",
    });

    if (result.isConfirmed) {
      try {
        setSaving(true);
        const response = await api.settings.update(settings);
        if (response.success) {
          showToast("บันทึกการตั้งค่าเรียบร้อยแล้ว");

          // อัปเดต global settings
          window.location.reload();
        }
      } catch (error) {
        showToast("เกิดข้อผิดพลาดในการบันทึกการตั้งค่า", "error");
      } finally {
        setSaving(false);
      }
    }
  };

  const handleReset = () => {
    loadSettings();
    showToast("รีเซ็ตการตั้งค่าเรียบร้อยแล้ว", "info");
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">กำลังโหลดการตั้งค่า...</p>
      </div>
    );
  }

  return (
    <div className="settings-page py-4">
      <div className="container-fluid">
        <div className="mb-4">
          <h1 className="h3 mb-0">
            <i className="fas fa-cog me-2"></i>
            ตั้งค่าระบบ
          </h1>
        </div>

        <div className="row">
          <div className="col-lg-8">
            <form onSubmit={handleSubmit}>
              {/* Basic Settings */}
              <div className="card mb-4">
                <div className="card-header card-header-gradient">
                  <h5 className="mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    ข้อมูลพื้นฐาน
                  </h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">ชื่อระบบ</label>
                    <input
                      type="text"
                      className="form-control"
                      name="system_name"
                      value={settings.system_name}
                      onChange={handleInputChange}
                      placeholder="ระบบลงนามถวายพระพรชัยมงคล"
                    />
                    <small className="text-muted">
                      ชื่อระบบที่จะแสดงในหน้าเว็บไซต์
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">หน่วยงาน/องค์กร</label>
                    <input
                      type="text"
                      className="form-control"
                      name="organization"
                      value={settings.organization}
                      onChange={handleInputChange}
                      placeholder="www.kruwirat.com"
                    />
                    <small className="text-muted">
                      ชื่อหน่วยงานที่ดูแลระบบ
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">URL โลโก้</label>
                    <div className="input-group">
                      <input
                        type="url"
                        className="form-control"
                        name="logo_url"
                        value={settings.logo_url}
                        onChange={handleInputChange}
                        placeholder="https://example.com/logo.jpg"
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => window.open(settings.logo_url, "_blank")}
                      >
                        <i className="fas fa-external-link-alt"></i>
                      </button>
                    </div>
                    <small className="text-muted">URL รูปภาพโลโก้ของระบบ</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">วันที่จัดงาน</label>
                    <input
                      type="date"
                      className="form-control"
                      name="event_date"
                      value={settings.event_date}
                      onChange={handleInputChange}
                    />
                    <small className="text-muted">วันเฉลิมพระชนมพรรษา</small>
                  </div>
                </div>
              </div>

              {/* System Settings */}
              <div className="card mb-4">
                <div className="card-header card-header-gradient">
                  <h5 className="mb-0">
                    <i className="fas fa-sliders-h me-2"></i>
                    การตั้งค่าระบบ
                  </h5>
                </div>
                <div className="card-body">
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="allowSignature"
                      name="allow_signature"
                      checked={settings.allow_signature === "true"}
                      onChange={handleInputChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="allowSignature"
                    >
                      อนุญาตให้ลงนามถวายพระพร
                    </label>
                    <br />
                    <small className="text-muted">
                      เปิด/ปิด การลงนามถวายพระพรจากบุคคลทั่วไป
                    </small>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                      disabled={saving}
                    >
                      <i className="fas fa-undo me-2"></i>
                      รีเซ็ต
                    </button>
                    <button
                      type="submit"
                      className="btn btn-gradient-primary"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          กำลังบันทึก...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          บันทึกการตั้งค่า
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Preview */}
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header card-header-gradient">
                <h5 className="mb-0">
                  <i className="fas fa-eye me-2"></i>
                  ตัวอย่างการแสดงผล
                </h5>
              </div>
              <div className="card-body">
                <div className="text-center mb-4">
                  <img
                    src={settings.logo_url || "https://via.placeholder.com/150"}
                    alt="Logo Preview"
                    style={{
                      width: "150px",
                      height: "150px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "3px solid #007CC7",
                    }}
                  />
                </div>

                <div className="mb-3">
                  <label className="fw-bold">ชื่อระบบ:</label>
                  <p>{settings.system_name || "ระบบลงนามถวายพระพรชัยมงคล"}</p>
                </div>

                <div className="mb-3">
                  <label className="fw-bold">หน่วยงาน:</label>
                  <p>{settings.organization || "www.kruwirat.com"}</p>
                </div>

                <div className="mb-3">
                  <label className="fw-bold">วันที่จัดงาน:</label>
                  <p>
                    {settings.event_date
                      ? new Date(settings.event_date).toLocaleDateString(
                          "th-TH",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )
                      : "12 สิงหาคม 2568"}
                  </p>
                </div>

                <div className="mb-3">
                  <label className="fw-bold">สถานะการลงนาม:</label>
                  <p>
                    {settings.allow_signature === "true" ? (
                      <span className="badge bg-success">
                        <i className="fas fa-check-circle me-1"></i>
                        เปิดให้ลงนาม
                      </span>
                    ) : (
                      <span className="badge bg-danger">
                        <i className="fas fa-times-circle me-1"></i>
                        ปิดการลงนาม
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* System Info */}
            <div className="card mt-4">
              <div className="card-header card-header-gradient">
                <h5 className="mb-0">
                  <i className="fas fa-info me-2"></i>
                  ข้อมูลระบบ
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-2">
                  <small className="text-muted">เวอร์ชัน:</small>
                  <p className="mb-1">1.0.0</p>
                </div>
                <div className="mb-2">
                  <small className="text-muted">พัฒนาโดย:</small>
                  <p className="mb-1">www.kruwirat.com</p>
                </div>
                <div className="mb-2">
                  <small className="text-muted">เทคโนโลยี:</small>
                  <p className="mb-1">React.js + Node.js + MySQL</p>
                </div>
                <div className="mb-2">
                  <small className="text-muted">อัปเดตล่าสุด:</small>
                  <p className="mb-0">
                    {new Date().toLocaleDateString("th-TH")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
